import { avatarEmoji } from '@cube-rush/shared';
import type { ReadonlySignal } from '@preact/signals';
import { ru } from '../../i18n/ru';
import type { HudDom } from './hudDom';
import type { HudModel } from './hudModel';

export interface RaceStripProps {
  hud: HudModel;
  dom: HudDom;
  speaking: ReadonlySignal<ReadonlySet<string>>;
}

/** Above this many levels only every fifth gets a notch (a long track would be all notches). */
const NOTCH_EVERY_LEVEL_UP_TO = 12;

/**
 * Everybody's progress through the whole race: one avatar per racer on a track with a
 * notch per level. The controller moves the avatars (HudDom.markers) a few times a second.
 *
 * Against the clock there is no finish line: the track is as long as the race so far (always a
 * level ahead of the leader) and every avatar carries its levels done, the score, from the
 * first one on.
 */
export function RaceStrip({ hud, dom, speaking }: RaceStripProps) {
  const racers = hud.strip.value;
  const talking = speaking.value;
  const span = hud.stripSpan.value;
  const timed = hud.timed;
  if (racers.length === 0) return null;
  const every = span > NOTCH_EVERY_LEVEL_UP_TO ? 5 : 1;
  const notches: number[] = [];
  for (let i = every; i < span; i += every) notches.push(i);
  return (
    <div
      class={`hud-strip${timed ? ' hud-strip--timed' : ''}`}
      role="img"
      aria-label={ru.game.raceProgress}
    >
      <div class="hud-strip__lane">
        <div class="hud-strip__track">
          {notches.map((i) => (
            <i key={i} class="hud-strip__notch" style={{ left: `${(i / span) * 100}%` }} />
          ))}
          {!timed && (
            <span class="hud-strip__flag" aria-hidden="true">
              🏁
            </span>
          )}
        </div>
        {racers.map((racer) => {
          const classes = [
            'hud-strip__racer',
            racer.me && 'hud-strip__racer--me',
            racer.finished && 'hud-strip__racer--finished',
            (racer.quit || racer.offline) && 'hud-strip__racer--gone',
            talking.has(racer.id) && 'hud-strip__racer--speaking',
          ]
            .filter(Boolean)
            .join(' ');
          // Against the clock the levels done, which stay on a racer who stopped (they still
          // count), from the first one on: at the start everybody stands in one spot, and a pile
          // of zeros says nothing. By levels a cross on a racer who gave up.
          const badge =
            racer.levels !== null
              ? racer.levels > 0 && (
                  <i class="hud-strip__badge hud-strip__badge--count">{racer.levels}</i>
                )
              : racer.quit && <i class="hud-strip__badge">✖</i>;
          return (
            <span
              key={racer.id}
              ref={(element) => dom.marker(racer.id, element)}
              class={classes}
              style={{ '--ring': racer.color }}
              title={
                racer.me ? ru.game.you : racer.quit ? `${racer.name} — ${ru.game.quit}` : racer.name
              }
            >
              {avatarEmoji(racer.avatar)}
              {badge}
            </span>
          );
        })}
      </div>
    </div>
  );
}
